import { FC, useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react'
import { MoreVertical, Pencil, Trash2, X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useIsMobile } from '../../hooks/useIsMobile'
import { Badge } from '../ui/badge'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu'
import type { WorkspaceType } from '../../services/workspaceService'

interface WorkspaceItemProps {
  id: string
  displayName: string
  type: WorkspaceType
  updatedAt: string
  isActive: boolean
  onClick: () => void
  onRename?: (id: string, newName: string) => void
  onClose?: (id: string) => void
  onDelete?: (id: string) => void
}

const formatRelativeTime = (dateStr: string): string => {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / 60_000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`

  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks < 4) return `${diffWeeks}w ago`

  const diffMonths = Math.floor(diffDays / 30)
  return `${diffMonths}mo ago`
}

const typeBadgeStyles: Record<WorkspaceType, string> = {
  admission: 'bg-clinical-teal/15 text-clinical-teal border-clinical-teal/30',
  flow: 'bg-clinical-sage/15 text-clinical-sage border-clinical-sage/30',
  chat: 'bg-clinical-amber/15 text-clinical-amber border-clinical-amber/30',
}

const typeLabels: Record<WorkspaceType, string> = {
  admission: 'ADM',
  flow: 'FLO',
  chat: 'CHT',
}

/**
 * Individual workspace item for the sidebar list.
 * Shows display name, type badge, relative time, and actions (rename, close).
 */
export const WorkspaceItem: FC<WorkspaceItemProps> = ({
  id,
  displayName,
  type,
  updatedAt,
  isActive,
  onClick,
  onRename,
  onClose,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [editValue, setEditValue] = useState(displayName)
  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobile()

  // Touch devices emulate :hover on first tap, so a hover-reveal (group-hover)
  // eats tap #1 and only fires onClick on tap #2. On mobile we drop hover entirely:
  // the row uses no hover background and the actions stay visible, so one tap selects.
  const actionVisibility = isMobile
    ? 'opacity-100'
    : 'opacity-0 group-hover:opacity-100 focus:opacity-100'

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  useEffect(() => {
    if (!isEditing) setEditValue(displayName)
  }, [displayName, isEditing])

  const handleStartEdit = () => {
    setEditValue(displayName)
    setIsEditing(true)
  }

  const handleSave = () => {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== displayName && onRename) {
      onRename(id, trimmed)
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditValue(displayName)
    setIsEditing(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value)
  }

  const handleClick = () => {
    if (!isEditing) onClick()
  }

  return (
    <div
      className={cn(
        'group relative rounded-lg transition-colors duration-150',
        'border-l-[3px]',
        isActive
          ? 'bg-accent border-l-primary'
          : cn('border-l-transparent', !isMobile && 'hover:bg-accent/50')
      )}
    >

      <button
        onClick={handleClick}
        className="w-full text-left px-3 py-2 flex items-center gap-2"
        disabled={isEditing}
      >
        {/* Type badge */}
        <Badge
          variant="outline"
          className={cn(
            'text-[9px] px-1.5 py-0 h-4 font-semibold flex-shrink-0',
            typeBadgeStyles[type]
          )}
        >
          {typeLabels[type]}
        </Badge>

        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'flex-1 text-sm bg-surface border border-border/50 rounded px-2 py-0.5',
              'focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50',
              'transition-all duration-150'
            )}
            maxLength={255}
          />
        ) : (
          <div className={cn('flex-1 min-w-0', isMobile ? 'pr-10' : 'pr-6')}>
            <span
              className={cn(
                'text-sm truncate block',
                isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
              )}
            >
              {displayName}
            </span>
            <span className="text-[10px] text-muted-foreground/60 block">
              {formatRelativeTime(updatedAt)}
            </span>
          </div>
        )}
      </button>

      {/* Desktop: standalone close button revealed on hover. On mobile the close
          action lives in the kebab menu instead (one tappable control per row). */}
      {!isEditing && !isMobile && onClose && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onClose(id)
          }}
          className={cn(
            'absolute right-7 top-1/2 -translate-y-1/2 p-0.5 rounded',
            actionVisibility,
            'hover:bg-destructive/10 hover:text-destructive',
            'transition-opacity duration-150',
            'focus:outline-none'
          )}
          aria-label="Close workspace"
          title="Close workspace"
        >
          <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
        </button>
      )}

      {/* Kebab menu for rename / delete (and close, on mobile) */}
      {!isEditing && (onRename || onDelete || (isMobile && onClose)) && (
        <DropdownMenu onOpenChange={(open) => { if (!open) setConfirmingDelete(false) }}>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'absolute right-1 top-1/2 -translate-y-1/2 rounded',
                isMobile ? 'p-2' : 'p-1',
                actionVisibility,
                'hover:bg-surface-elevated',
                'transition-opacity duration-150',
                'focus:outline-none'
              )}
              onClick={(e) => e.stopPropagation()}
              aria-label="Workspace options"
            >
              <MoreVertical
                className={cn(
                  isMobile ? 'h-5 w-5 text-foreground/70' : 'h-3.5 w-3.5 text-muted-foreground'
                )}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="right" className="min-w-[120px]">
            {onRename && (
              <DropdownMenuItem onClick={handleStartEdit} className="gap-2 text-sm">
                <Pencil className="h-3.5 w-3.5" />
                <span>Rename</span>
              </DropdownMenuItem>
            )}
            {isMobile && onClose && (
              <DropdownMenuItem onClick={() => onClose(id)} className="gap-2 text-sm">
                <X className="h-3.5 w-3.5" />
                <span>Close</span>
              </DropdownMenuItem>
            )}
            {(onRename || (isMobile && onClose)) && onDelete && <DropdownMenuSeparator />}
            {onDelete && !confirmingDelete && (
              <DropdownMenuItem
                onClick={(e) => { e.preventDefault(); setConfirmingDelete(true) }}
                className="gap-2 text-sm text-destructive focus:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span>Delete</span>
              </DropdownMenuItem>
            )}
            {onDelete && confirmingDelete && (
              <div className="px-2 py-1.5">
                <p className="text-xs text-muted-foreground mb-2">Are you sure?</p>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => { onDelete(id); setConfirmingDelete(false) }}
                    className={cn(
                      'flex-1 px-2 py-1 text-xs font-medium rounded',
                      'bg-destructive text-destructive-foreground',
                      'hover:bg-destructive/90 transition-colors'
                    )}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    className={cn(
                      'flex-1 px-2 py-1 text-xs font-medium rounded',
                      'bg-surface border border-border/50',
                      'hover:bg-accent transition-colors'
                    )}
                  >
                    No
                  </button>
                </div>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
